import { useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import './Sidebar.css'
import { IoHomeOutline } from 'react-icons/io5'
import {
  LuComponent, LuLayers, LuTicket, LuUserCog, LuDatabase, LuSettings, LuShield, LuLayoutDashboard, LuActivity,
  LuRuler, LuBuilding2, LuGrid, LuAppWindow, LuNetwork, LuLightbulb, LuMap, LuShieldCheck, LuFileBarChart,
  LuTarget, LuListChecks, LuColumns, LuBookOpen,
} from 'react-icons/lu'
import { PiCertificate, PiTreeStructure } from 'react-icons/pi'
import { CiViewTable } from 'react-icons/ci'
import React from 'react'

import { FaBullseye, FaLink, FaSitemap, FaExternalLinkAlt, FaFileAlt, FaChevronDown, FaChevronRight, FaTasks, FaBuilding, FaServer, FaBriefcase, FaCalendarAlt, FaCompass, FaDollarSign, FaExclamationTriangle, FaCodeBranch, FaShieldAlt, FaLayerGroup, FaProjectDiagram, FaBoxOpen, FaList, FaRobot, FaFolder, FaCheckSquare, FaClipboardCheck, FaHistory, FaBook, FaChartBar, FaBullhorn, FaUsers } from 'react-icons/fa'

import { getV2Token } from '../../utils/v2AuthUtils'
import { AiTwotoneSecurityScan } from 'react-icons/ai'
import { CONFIGURATION_SECTION_NAV } from '../../navigation/configurationSectionNav'
import { useTenant } from '../../context/TenantContext'
import { capabilitiesFor } from '../../types/rootEntity'
// Partner Portal sidebar (partnerLinks array + PartnerSidebarBadge) removed —
// the Partner Portal now lives in the standalone partner_portal_frontend app.

interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
  language: 'en' | 'ar'
}

interface SidebarLink {
  page: string
  labelKey: string
  /** Optional muted one-line hint under the label (expanded sidebar / mobile). */
  descriptionKey?: string
  href: string
  icon: (props: any) => React.ReactElement
  external?: boolean
  externalUrl?: string | null
  children?: SidebarLink[]
  /** When set, only show this link if the caller's archetype has the named
   *  capability per src/types/rootEntity.ts. */
  archetypeGate?: 'hasPractitioners' | 'hasPlaybookIp' | 'ownsEvidenceVault' | 'hasAuditWorkspace' | 'canFederateOutbound' | 'canFederateInbound' | 'canManagePartners' | 'canManageMemberships' | 'canManageAllTenants'
  /** Phase 4: when true, only show this link if the caller's tenant has
   *  subtree scope (non-leaf tenant) — leaf branches have nothing to administer. */
  requiresSubtree?: boolean
  /** Phase 5: when true, only show this link if the caller's tenant HAS a
   *  parent (tenantDepth > 0) — root tenants aren't branches. */
  requiresSubTenantDepth?: boolean
}

// Partner Portal sidebar (partnerLinks) removed — moved to the standalone
// partner_portal_frontend app.

/** Icons for Configuration submenu (must match CONFIGURATION_SECTION_NAV pages) */
const configurationSectionIconByPage: Record<string, (props: any) => React.ReactElement> = {
  'configuration-dashboard': (props: any) => <LuLayoutDashboard {...props} />,
  'document-categories': (props: any) => <FaFileAlt {...props} />,
  'services-status': (props: any) => <LuSettings {...props} />,
  Activities: (props: any) => <FaTasks {...props} />,
  ControlReferencePage: (props: any) => <FaLink {...props} />,
  'FinancialImpactsPage': (props: any) => <FaDollarSign {...props} />,
  EventTypePage: (props: any) => <FaCalendarAlt {...props} />,
  AccountTypePage: (props: any) => <FaBuilding {...props} />,
  process: (props: any) => <FaTasks {...props} />,
  CreateSubBusinessUnitPage: (props: any) => <FaBuilding {...props} />,
  CreateSystem: (props: any) => <FaServer {...props} />,
  CreateJobTitle: (props: any) => <FaBriefcase {...props} />,
  CreateDiscoveredType: (props: any) => <FaCompass {...props} />,
  CreateCurrencyPage: (props: any) => <FaDollarSign {...props} />,
  CreateRootCause: (props: any) => <FaExclamationTriangle {...props} />,
  CreateSubFunction: (props: any) => <FaCodeBranch {...props} />,
  CreateRiskCategory: (props: any) => <FaShieldAlt {...props} />,
  'lifecycle-stages': (props: any) => <LuSettings {...props} />,
  criticalities: (props: any) => <LuSettings {...props} />,
  'asset-types': (props: any) => <LuSettings {...props} />,
  'asset-classifications': (props: any) => <LuSettings {...props} />,
  function: (props: any) => <PiCertificate {...props} />,
  'policy-categories': (props: any) => <FaFolder {...props} />,
  'policy-settings': (props: any) => <LuSettings {...props} />,
  'llm-routing': (props: any) => <FaRobot {...props} />,
}

const configurationSidebarChildren: SidebarLink[] = CONFIGURATION_SECTION_NAV.map((item) => ({
  page: item.page,
  labelKey: item.labelKey,
  href: item.href,
  icon: configurationSectionIconByPage[item.page] ?? ((props: any) => <LuSettings {...props} />),
}))

// Update links array to include icon components as functions
// Order: Home → Compliance → Enterprise → Catalogs → Data (Document) → Reporting → User Settings → Configuration
const links: SidebarLink[] = [
  {
    page: 'dashboard-section',
    labelKey: 'Sidebar.DashboardSection',
    href: '#',
    icon: (props: any) => <LuLayoutDashboard {...props} />,
    children: [
      {
        page: 'dashboard-home',
        labelKey: 'Sidebar.Home',
        descriptionKey: 'Sidebar.DashboardHomeDesc',
        href: '/',
        icon: (props: any) => <IoHomeOutline {...props} />,
      },
      {
        page: 'role-dashboard',
        labelKey: 'Sidebar.MyDashboard',
        href: '/dashboards/roles',
        icon: (props: any) => <LuLayoutDashboard {...props} />,
      },
      {
        page: 'risk-controls-matrix',
        labelKey: 'Sidebar.RiskControlsMatrix',
        descriptionKey: 'Sidebar.RiskMatrixDesc',
        href: '/dashboard/risk-controls-matrix',
        icon: (props: any) => <LuGrid {...props} />,
      },
      // Receivables Hub — single entry; hosts dashboards, live operations,
      // and the cycle-time simulator as internal tabs to keep the sidebar tidy.
      {
        page: 'receivables-hub',
        labelKey: 'Sidebar.ReceivablesHub',
        descriptionKey: 'Sidebar.ReceivablesHubDesc',
        href: '/analytics/receivables',
        icon: (props: any) => <LuFileBarChart {...props} />,
      },
    ],
  },
  {
    page: 'process-module',
    labelKey: 'Sidebar.ProcessModule',
    href: '#',
    icon: (props: any) => <FaList {...props} />,
    children: [
      { page: 'instances', labelKey: 'Sidebar.Processes', href: '/instances', icon: (props: any) => <LuLayers {...props} /> },
      { page: 'tasks', labelKey: 'Sidebar.TaskInbox', href: '/tasks', icon: (props: any) => <FaTasks {...props} /> },
      { page: 'monitor', labelKey: 'Sidebar.LiveMonitor', href: '/monitor', icon: (props: any) => <LuActivity {...props} /> },
      { page: 'metrics', labelKey: 'Sidebar.Metrics', href: '/metrics', icon: (props: any) => <FaChartBar {...props} /> },
      { page: 'agents', labelKey: 'Sidebar.Agents', href: '/agents', icon: (props: any) => <FaRobot {...props} /> },
      { page: 'process-viewer', labelKey: 'Sidebar.ProcessSteps', href: '/process/viewer', icon: (props: any) => <FaProjectDiagram {...props} /> },
    ],
  },
  {
    page: 'trustlens',
    labelKey: 'Sidebar.TrustLens',
    href: '#',
    icon: (props: any) => <LuShieldCheck {...props} />,
    children: [
      { page: 'trustlens-dashboard', labelKey: 'Sidebar.TrustLensDashboard', href: '/trustlens/dashboard', icon: (props: any) => <LuLayoutDashboard {...props} /> },
      { page: 'trustlens-profile', labelKey: 'Sidebar.TrustLensProfile', href: '/trustlens/profile', icon: (props: any) => <FaBuilding {...props} /> },
      { page: 'trustlens-assessment', labelKey: 'Sidebar.TrustLensAssessment', href: '/trustlens/assessment', icon: (props: any) => <FaTasks {...props} /> },
      { page: 'trustlens-l1', labelKey: 'Sidebar.TrustLensL1', href: '/trustlens/posture/l1', icon: (props: any) => <FaBullseye {...props} /> },
      { page: 'trustlens-l2', labelKey: 'Sidebar.TrustLensL2', href: '/trustlens/posture/l2', icon: (props: any) => <LuListChecks {...props} /> },
      { page: 'trustlens-l3', labelKey: 'Sidebar.TrustLensL3', href: '/trustlens/posture/l3', icon: (props: any) => <LuBookOpen {...props} /> },
      { page: 'trustlens-l4', labelKey: 'Sidebar.TrustLensL4', href: '/trustlens/posture/l4', icon: (props: any) => <FaCalendarAlt {...props} /> },
      { page: 'trustlens-l5', labelKey: 'Sidebar.TrustLensL5', href: '/trustlens/posture/l5', icon: (props: any) => <LuShieldCheck {...props} /> },
      { page: 'trustlens-report', labelKey: 'Sidebar.TrustLensReport', href: '/trustlens/report', icon: (props: any) => <FaChartBar {...props} /> },
      { page: 'trustlens-invitations', labelKey: 'Sidebar.TrustLensInvitations', href: '/trustlens/invitations', icon: (props: any) => <FaUsers {...props} /> },
    ],
  },
  {
    page: 'compliance',
    labelKey: 'Sidebar.ComplianceMain',
    href: '#',
    icon: (props: any) => <LuComponent {...props} />,
    children: [
      { page: 'compliance-dashboard', labelKey: 'Sidebar.Dashboard', href: '/compliances/dashboard', icon: (props: any) => <LuLayoutDashboard {...props} /> },
      { page: 'standards', labelKey: 'Sidebar.Reference', href: '/standards', icon: (props: any) => <CiViewTable {...props} /> },
      { page: 'objectives', labelKey: 'Sidebar.Objectives', href: '/objective', icon: (props: any) => <FaBullseye {...props} /> },
      { page: 'assessment', labelKey: 'Sidebar.Assessment', href: '/assessment', icon: (props: any) => <PiCertificate {...props} /> },
      { page: 'compliances', labelKey: 'Sidebar.Compliance', href: '/compliances', icon: (props: any) => <LuComponent {...props} /> },
      {
        page: 'policy-module',
        labelKey: 'Sidebar.PolicyModule',
        href: '#',
        icon: (props: any) => <FaFileAlt {...props} />,
        children: [
          { page: 'policy-library', labelKey: 'Sidebar.PolicyLibrary', href: '/policy', icon: (props: any) => <FaFileAlt {...props} /> },
          { page: 'policy-obligations', labelKey: 'Sidebar.RegulatoryObligations', href: '/policy/obligations', icon: (props: any) => <PiCertificate {...props} /> },
          { page: 'policy-approvals', labelKey: 'Sidebar.ApprovalWorkflow', href: '/policy/approvals', icon: (props: any) => <FaCheckSquare {...props} /> },
          { page: 'policy-attestation', labelKey: 'Sidebar.Attestation', href: '/policy/attestation', icon: (props: any) => <FaClipboardCheck {...props} /> },
          { page: 'policy-versions', labelKey: 'Sidebar.PolicyVersionControl', href: '/policy/versions', icon: (props: any) => <FaHistory {...props} /> },
        ],
      },
      { page: 'announcements', labelKey: 'Sidebar.Announcements', href: '/announcements', icon: (props: any) => <LuTicket {...props} /> },
      { page: 'tickets', labelKey: 'Sidebar.Tickets', href: '/ticket', icon: (props: any) => <LuTicket {...props} /> },
      { page: 'impact-area', labelKey: 'Sidebar.ImpactArea', href: '/impactarea', icon: (props: any) => <FaLayerGroup {...props} /> },
      {
        page: 'survey-module',
        labelKey: 'Sidebar.Survey',
        href: '#',
        icon: (props: any) => <PiCertificate {...props} />,
        children: [
          { page: 'surveys-list', labelKey: 'Sidebar.Surveys', href: '/surveys', icon: (props: any) => <FaList {...props} /> },
          { page: 'create-survey', labelKey: 'Sidebar.CreateSurvey', href: '/create-survey', icon: (props: any) => <FaCheckSquare {...props} /> },
          { page: 'bank-questions', labelKey: 'Sidebar.BankQuestions', href: '/bankQuestions', icon: (props: any) => <FaClipboardCheck {...props} /> },
          { page: 'RCSA', labelKey: 'Sidebar.RCSA', href: '/rcsa-compliance', icon: (props: any) => <FaChartBar {...props} /> },

        ],
      },
    ],
  },
  /** Supplemental compliance hub (EG CERT–style shortcuts: dashboard, reports, standards, tickets, etc.) */
 
  /* Enterprise Architecture (/ea/*) — hidden from sidebar; re-enable when needed
  {
    page: 'enterprise-architecture',
    labelKey: 'Sidebar.EnterpriseArchitecture',
    href: '#',
    icon: (props: any) => <PiTreeStructure {...props} />,
    children: [
      { page: 'ea-dashboard', labelKey: 'Sidebar.EADashboard', href: '/ea/dashboard', icon: (props: any) => <LuLayoutDashboard {...props} /> },
      { page: 'ea-principles', labelKey: 'Sidebar.EAPrinciples', href: '/ea/principles', icon: (props: any) => <FaProjectDiagram {...props} /> },
      { page: 'ea-applications', labelKey: 'Sidebar.EAApplications', href: '/ea/applications', icon: (props: any) => <LuLayers {...props} /> },
      { page: 'ea-work-packages', labelKey: 'Sidebar.EAWorkPackages', href: '/ea/work-packages', icon: (props: any) => <FaBoxOpen {...props} /> },
      { page: 'ea-scope-items', labelKey: 'Sidebar.EAScopeItems', href: '/ea/scope-items', icon: (props: any) => <FaTasks {...props} /> },
      { page: 'ea-relations-tree', labelKey: 'Sidebar.EARelationsTree', href: '/ea/relations-tree', icon: (props: any) => <PiTreeStructure {...props} /> },
    ],
  },
  */
  {
    page: 'Enterprise Architecture',
    labelKey: 'Sidebar.Business',
    href: '#',
    icon: (props: any) => <FaBuilding {...props} />,
    children: [
      { page: 'ea-canvas-exec', labelKey: 'Sidebar.EaCanvasExecutive', href: '/ea-canvas', icon: (props: any) => <LuLayoutDashboard {...props} /> },
      { page: 'ea-canvas-blueprint', labelKey: 'Sidebar.EaCanvasEnterpriseBlueprint', href: '/ea-canvas/enterprise-blueprint', icon: (props: any) => <LuNetwork {...props} /> },
      { page: 'ea-canvas-north-stars', labelKey: 'Sidebar.EaCanvasNorthStars', href: '/ea-canvas/north-stars', icon: (props: any) => <LuTarget {...props} /> },
      { page: 'ea-canvas-pillars', labelKey: 'Sidebar.EaCanvasPillars', href: '/ea-canvas/pillars', icon: (props: any) => <LuColumns {...props} /> },
      { page: 'ea-canvas-anatomy', labelKey: 'Sidebar.EaCanvasAnatomy', href: '/ea-canvas/anatomy', icon: (props: any) => <LuBuilding2 {...props} /> },
      { page: 'ea-canvas-scales', labelKey: 'Sidebar.EaCanvasScales', href: '/ea-canvas/scales', icon: (props: any) => <LuRuler {...props} /> },
      { page: 'ea-canvas-heatmap', labelKey: 'Sidebar.EaCanvasHeatmap', href: '/ea-canvas/heatmap', icon: (props: any) => <LuGrid {...props} /> },
      { page: 'ea-canvas-findings', labelKey: 'Sidebar.EaCanvasFindings', href: '/ea-canvas/findings', icon: (props: any) => <LuLightbulb {...props} /> },
      { page: 'ea-canvas-app-matrix', labelKey: 'Sidebar.EaCanvasAppMatrix', href: '/ea-canvas/app-matrix', icon: (props: any) => <LuAppWindow {...props} /> },
      { page: 'ea-canvas-roadmap', labelKey: 'Sidebar.EaCanvasRoadmap', href: '/ea-canvas/roadmap', icon: (props: any) => <LuMap {...props} /> },
      { page: 'ea-canvas-phases', labelKey: 'Sidebar.EaCanvasPhaseTracker', href: '/ea-canvas/phases', icon: (props: any) => <LuListChecks {...props} /> },
      { page: 'ea-canvas-governance', labelKey: 'Sidebar.EaCanvasGovernance', href: '/ea-canvas/governance', icon: (props: any) => <LuShieldCheck {...props} /> },
      { page: 'ea-canvas-reports', labelKey: 'Sidebar.EaCanvasReports', href: '/ea-canvas/reports', icon: (props: any) => <LuFileBarChart {...props} /> },
      { page: 'ea-canvas-config', labelKey: 'Sidebar.EaCanvasDataManagement', href: '/ea-canvas/configuration', icon: (props: any) => <LuSettings {...props} /> },
    ],
  },
  {
    page: 'enterprise-risk-management',
    labelKey: 'Sidebar.EnterpriseRiskManagement',
    href: '#',
    icon: (props: any) => <FaExternalLinkAlt {...props} />,
    external: true,
    externalUrl: null
  },
  {
    page: 'catalogs',
    labelKey: 'Sidebar.Catalogs',
    href: '#',
    icon: (props: any) => <CiViewTable {...props} />,
    children: [
      { page: 'catalogs-dashboard', labelKey: 'Sidebar.Dashboard', href: '/catalogs/dashboard', icon: (props: any) => <LuLayoutDashboard {...props} /> },
      { page: 'services-catalog', labelKey: 'Sidebar.ServicesCatalog',
         href: '/catalogs/services', icon: (props: any) => <LuLayers {...props} /> },
      { page: 'resources', labelKey: 'Sidebar.ResourcesManagement', href: '/catalogs/resources', icon: (props: any) => <LuLayers {...props} /> },
      { page: 'controls', labelKey: 'Sidebar.ControlCatalog', href: '/controls', icon: (props: any) => <AiTwotoneSecurityScan {...props} /> },
      { page: 'risk-catalog', labelKey: 'Sidebar.RiskCatalog', href: '/risks', icon: (props: any) => <FaShieldAlt {...props} /> },
      { page: 'collection-sops', labelKey: 'Sidebar.CollectionSops', href: '/catalogs/collection-sops', icon: (props: any) => <FaClipboardCheck {...props} /> },
    
    ],
  },
  {
    page: 'documents',
    labelKey: 'Sidebar.DocumentManagement',
    href: '/documents',
    icon: (props: any) => <FaFileAlt {...props} />
  },
  {
    page: 'reporting',
    labelKey: 'Sidebar.ReportingSystem',
    href: '/reporting',
    icon: (props: any) => <FaChartBar {...props} />
  },
  {
    page: 'tenant-compliance',
    labelKey: 'Sidebar.TenantComplianceGroup',
    href: '#',
    icon: (props: any) => <LuShieldCheck {...props} />,
    children: [
      {
        page: 'tenant-compliance-evidence',
        labelKey: 'Sidebar.OrganizationsEvidence',
        href: '/evidence',
        icon: (props: any) => <LuFileBarChart {...props} />,
        archetypeGate: 'ownsEvidenceVault',
      },
      {
        page: 'tenant-compliance-trust-center',
        labelKey: 'Sidebar.OrganizationsTrustCenter',
        href: '/trust-center',
        icon: (props: any) => <LuShieldCheck {...props} />,
        archetypeGate: 'ownsEvidenceVault',
      },
    ],
  },
  {
    page: 'playbooks',
    labelKey: 'Sidebar.Playbooks',
    href: '#',
    icon: (props: any) => <LuBookOpen {...props} />,
    children: [
      {
        page: 'playbooks-library',
        labelKey: 'Sidebar.PlaybooksLibrary',
        href: '/playbooks',
        icon: (props: any) => <LuListChecks {...props} />,
      },
      {
        page: 'playbooks-author',
        labelKey: 'Sidebar.PlaybooksAuthor',
        href: '/playbooks/new',
        icon: (props: any) => <LuLightbulb {...props} />,
        archetypeGate: 'hasPlaybookIp',
      },
    ],
  },
  // Partner Portal sidebar section removed — moved to the standalone partner_portal_frontend app.
  {
    page: 'user-settings',
    labelKey: 'Sidebar.UserSettings',
    href: '#',
    icon: (props: any) => <LuUserCog {...props} />,
    children: [
      { page: 'users', labelKey: 'Sidebar.UserManagement', href: '/user', icon: (props: any) => <LuUserCog {...props} /> },
      { page: 'organization-chart', labelKey: 'Sidebar.OrganizationChart', href: '/organization-chart', icon: (props: any) => <FaSitemap {...props} /> },
      {
        page: 'user-settings-permission',
        labelKey: 'Sidebar.Permission',
        href: '#',
        icon: (props: any) => <LuShield {...props} />,
        children: [
          { page: 'user-permissions', labelKey: 'Sidebar.UserPermissions', href: '/user/permissions', icon: (props: any) => <LuSettings {...props} /> },
          { page: 'groups-pages', labelKey: 'Sidebar.GroupsAndPages', href: '/permission/groups-pages', icon: (props: any) => <LuShield {...props} /> },
        ],
      },
    ],
  },
  {
    page: 'configuration',
    labelKey: 'Sidebar.Configuration',
    href: '#',
    icon: (props: any) => <LuSettings {...props} />,
    children: configurationSidebarChildren,
  },
  {
    page: 'audit',
    labelKey: 'Sidebar.Audit',
    href: '#',
    icon: (props: any) => <LuActivity {...props} />,
    children: [
      { page: 'audit-activity-log', labelKey: 'Sidebar.ActivityLog', href: '/audit/activity-log', icon: (props: any) => <FaHistory {...props} /> },
      { page: 'iams-dashboard', labelKey: 'Sidebar.IamsDashboard', href: '/audit/iams', icon: (props: any) => <LuLayoutDashboard {...props} /> },
      { page: 'iams-canvas', labelKey: 'Sidebar.IamsCanvas', href: '/audit/iams/canvas', icon: (props: any) => <FaProjectDiagram {...props} /> },
      { page: 'iams-universe', labelKey: 'Sidebar.IamsUniverse', href: '/audit/iams/universe', icon: (props: any) => <FaCompass {...props} /> },
      { page: 'iams-plan-composer', labelKey: 'Sidebar.IamsPlanComposer', href: '/audit/iams/plan-composer', icon: (props: any) => <FaCalendarAlt {...props} /> },
      { page: 'iams-scenario', labelKey: 'Sidebar.IamsScenario', href: '/audit/iams/scenario-planner', icon: (props: any) => <FaCodeBranch {...props} /> },
      { page: 'iams-capacity', labelKey: 'Sidebar.IamsCapacity', href: '/audit/iams/capacity', icon: (props: any) => <FaUsers {...props} /> },
      { page: 'iams-engagements', labelKey: 'Sidebar.IamsEngagements', href: '/audit/iams/engagements', icon: (props: any) => <FaClipboardCheck {...props} /> },
      { page: 'iams-fieldwork', labelKey: 'Sidebar.IamsFieldwork', href: '/audit/iams/fieldwork', icon: (props: any) => <LuActivity {...props} /> },
      { page: 'iams-checklists', labelKey: 'Sidebar.IamsChecklists', href: '/audit/iams/checklists', icon: (props: any) => <FaCheckSquare {...props} /> },
      { page: 'iams-rc-library', labelKey: 'Sidebar.IamsRcLibrary', href: '/audit/iams/rc-library', icon: (props: any) => <FaBook {...props} /> },
      { page: 'iams-test-plan', labelKey: 'Sidebar.IamsTestPlan', href: '/audit/iams/test-plan', icon: (props: any) => <LuListChecks {...props} /> },
      { page: 'iams-working-paper', labelKey: 'Sidebar.IamsWorkingPaper', href: '/audit/iams/working-paper', icon: (props: any) => <FaFileAlt {...props} /> },
      { page: 'iams-evidence', labelKey: 'Sidebar.IamsEvidence', href: '/audit/iams/evidence', icon: (props: any) => <FaFolder {...props} /> },
      { page: 'iams-anomaly', labelKey: 'Sidebar.IamsAnomaly', href: '/audit/iams/anomaly-triage', icon: (props: any) => <FaExclamationTriangle {...props} /> },
      { page: 'iams-review-queue', labelKey: 'Sidebar.IamsReviewQueue', href: '/audit/iams/review-queue', icon: (props: any) => <LuListChecks {...props} /> },
      { page: 'iams-findings', labelKey: 'Sidebar.IamsFindings', href: '/audit/iams/findings', icon: (props: any) => <FaExclamationTriangle {...props} /> },
      { page: 'iams-action-plans', labelKey: 'Sidebar.IamsActionPlans', href: '/audit/iams/action-plans', icon: (props: any) => <FaTasks {...props} /> },
      { page: 'iams-risk-updates', labelKey: 'Sidebar.IamsRiskUpdates', href: '/audit/iams/risk-updates', icon: (props: any) => <FaShieldAlt {...props} /> },
      { page: 'iams-monitor', labelKey: 'Sidebar.IamsMonitor', href: '/audit/iams/monitor', icon: (props: any) => <FaChartBar {...props} /> },
      { page: 'iams-analytics', labelKey: 'Sidebar.IamsAnalytics', href: '/audit/iams/analytics', icon: (props: any) => <FaChartBar {...props} /> },
      { page: 'iams-eng-report', labelKey: 'Sidebar.IamsEngReport', href: '/audit/iams/engagement-report', icon: (props: any) => <FaFileAlt {...props} /> },
      { page: 'iams-ac-pack', labelKey: 'Sidebar.IamsAcPack', href: '/audit/iams/ac-pack', icon: (props: any) => <FaBriefcase {...props} /> },
      { page: 'iams-board-pack', labelKey: 'Sidebar.IamsBoardPack', href: '/audit/iams/board-pack', icon: (props: any) => <FaBuilding {...props} /> },
      { page: 'iams-ac-dashboard', labelKey: 'Sidebar.IamsAcDashboard', href: '/audit/iams/ac-dashboard', icon: (props: any) => <FaChartBar {...props} /> },
      { page: 'iams-ac-plan-review', labelKey: 'Sidebar.IamsAcPlanReview', href: '/audit/iams/ac-plan-review', icon: (props: any) => <FaCalendarAlt {...props} /> },
      { page: 'iams-auditee-portal', labelKey: 'Sidebar.IamsAuditeePortal', href: '/audit/iams/auditee-portal', icon: (props: any) => <FaBullhorn {...props} /> },
      { page: 'iams-auditee-requests', labelKey: 'Sidebar.IamsAuditeeRequests', href: '/audit/iams/auditee-requests', icon: (props: any) => <FaTasks {...props} /> },
      { page: 'iams-auditee-notify', labelKey: 'Sidebar.IamsAuditeeNotify', href: '/audit/iams/auditee-notify', icon: (props: any) => <FaBullhorn {...props} /> },
      { page: 'iams-agent-runs', labelKey: 'Sidebar.IamsAgentRuns', href: '/audit/iams/agent-runs', icon: (props: any) => <FaRobot {...props} /> },
      { page: 'iams-copilot', labelKey: 'Sidebar.IamsCopilot', href: '/audit/iams/copilot-connector', icon: (props: any) => <FaRobot {...props} /> },
      { page: 'iams-trust-center', labelKey: 'Sidebar.IamsTrustCenter', href: '/audit/iams/trust-center', icon: (props: any) => <FaShieldAlt {...props} /> },
      { page: 'iams-admin', labelKey: 'Sidebar.IamsAdmin', href: '/audit/iams/admin', icon: (props: any) => <LuSettings {...props} /> },
      { page: 'iams-workflows', labelKey: 'Sidebar.IamsWorkflows', href: '/audit/iams/admin/workflows', icon: (props: any) => <FaProjectDiagram {...props} /> },
      { page: 'iams-bulk-import', labelKey: 'Sidebar.IamsBulkImport', href: '/audit/iams/admin/import', icon: (props: any) => <FaFileAlt {...props} /> },
    ],
  },


  /* Sup-Compliance menu — hidden from sidebar; re-enable when needed
  {
    page: 'sup-compliance',
    labelKey: 'Sidebar.SupCompliance',
    href: '#',
    icon: (props: any) => <LuShieldCheck {...props} />,
    children: [
      { page: 'sup-dashboard', labelKey: 'Sidebar.Dashboard', href: '/compliances/dashboard', icon: (props: any) => <LuLayoutDashboard {...props} /> },
      { page: 'sup-reports', labelKey: 'Sidebar.Reports', href: '/reportSup', icon: (props: any) => <LuFileBarChart {...props} /> },
      // { page: 'sup-standards', labelKey: 'Sidebar.Reference', href: '/StandardsSupCompliance', icon: (props: any) => <LuGrid {...props} /> },
      { page: 'sup-tickets', labelKey: 'Sidebar.Tickets', href: '/ticketsSupCompliance', icon: (props: any) => <LuTicket {...props} /> },
      { page: 'sup-assessment', labelKey: 'Sidebar.Assessment', href: '/assessmentSup', icon: (props: any) => <FaChartBar {...props} /> },
      { page: 'sup-certifications', labelKey: 'Sidebar.Certifications', href: '/certificationsSup', icon: (props: any) => <PiCertificate {...props} /> },
      { page: 'sup-announcements', labelKey: 'Sidebar.Announcements', href: '/announcementSup', icon: (props: any) => <FaBullhorn {...props} /> },
      { page: 'sup-institutions', labelKey: 'Sidebar.SupComplianceInstitutions', href: '/institutionSup', icon: (props: any) => <LuBuilding2 {...props} /> },
    ],
  },
  */
  {
    page: 'organizations',
    labelKey: 'Sidebar.Organizations',
    href: '#',
    icon: (props: any) => <LuBuilding2 {...props} />,
    children: [
      {
        page: 'organizations-home',
        labelKey: 'Sidebar.OrganizationsHome',
        href: '/tenants/tree',
        icon: (props: any) => <FaSitemap {...props} />,
      },
      {
        page: 'tenant-compliance-standards',
        labelKey: 'Sidebar.OrganizationsStandards',
        href: '/tenants/standards',
        icon: (props: any) => <LuShieldCheck {...props} />,
      },
      {
        page: 'organizations-tickets',
        labelKey: 'Sidebar.OrganizationsTickets',
        href: '/tenants/tickets',
        icon: (props: any) => <LuTicket {...props} />,
      },
      // {
      //   page: 'organizations-federations',
      //   labelKey: 'Sidebar.OrganizationsFederations',
      //   href: '/federations',
      //   icon: (props: any) => <LuNetwork {...props} />,
      // },
      {
        page: 'organizations-settings',
        labelKey: 'Sidebar.OrganizationsSettings',
        href: '/tenants/settings',
        icon: (props: any) => <LuSettings {...props} />,
      },
    ],
  },
  /* Admin Partners directory removed — pages were demo-only; rebuild against
     real /api/admin/partners/* endpoints when they exist. */
  /* User Guide — hidden from sidebar; re-enable when needed
  {
    page: 'guide',
    labelKey: 'Sidebar.UserGuide',
    href: '/guide',
    icon: (props: any) => <FaBook {...props} />
  },
  */
  // Pianat Admin (platform_operator console) lives in the standalone
  // pianat_admin_frontend app — intentionally absent from the customer sidebar.
  // Branch (Phase 5) — sub-tenant operations: inter-branch collaboration +
  // the daily Branch Ops agent. Only for tenants WITH a parent.
  {
    page: 'sub-tenant-section',
    labelKey: 'Sidebar.Branch',
    href: '#',
    icon: (props: any) => <FaBuilding {...props} />,
    requiresSubTenantDepth: true,
    children: [
      {
        page: 'st-inter-branch',
        labelKey: 'Sidebar.BranchInterBranch',
        href: '/sub-tenant/inter-branch',
        icon: (props: any) => <LuTicket {...props} />,
      },
      {
        page: 'st-branch-ops',
        labelKey: 'Sidebar.BranchOpsAgent',
        href: '/sub-tenant/branch-ops-agent',
        icon: (props: any) => <FaRobot {...props} />,
      },
    ],
  },
  // Tenant Admin (Phase 4) — parent tenants managing their sub-tenant tree.
  // Double-gated: canManageMemberships capability + subtree scope; backend
  // enforces the same via @TenantOwnerOnly so hiding here is UX, not security.
  {
    page: 'tenant-admin-section',
    labelKey: 'Sidebar.TenantAdmin',
    href: '#',
    icon: (props: any) => <FaSitemap {...props} />,
    archetypeGate: 'canManageMemberships',
    requiresSubtree: true,
    children: [
      {
        page: 'ta-subtree',
        labelKey: 'Sidebar.TenantAdminSubtree',
        href: '/tenant-admin/subtree',
        icon: (props: any) => <FaSitemap {...props} />,
      },
      {
        page: 'ta-memberships',
        labelKey: 'Sidebar.TenantAdminMemberships',
        href: '/tenant-admin/memberships',
        icon: (props: any) => <FaUsers {...props} />,
      },
      {
        page: 'ta-rollup',
        labelKey: 'Sidebar.TenantAdminRollup',
        href: '/tenant-admin/rollup',
        icon: (props: any) => <CiViewTable {...props} />,
      },
      {
        page: 'ta-rollup-findings',
        labelKey: 'Sidebar.TenantAdminRollupFindings',
        href: '/tenant-admin/rollup/findings',
        icon: (props: any) => <FaFileAlt {...props} />,
      },
      {
        page: 'ta-rollup-spend',
        labelKey: 'Sidebar.TenantAdminRollupSpend',
        href: '/tenant-admin/rollup/spend',
        icon: (props: any) => <FaBook {...props} />,
      },
      {
        page: 'ta-insights',
        labelKey: 'Sidebar.TenantAdminInsights',
        href: '/tenant-admin/insights',
        icon: (props: any) => <FaRobot {...props} />,
      },
    ],
  },
  // Consultants (Client Portal) — kept LAST in the menu. Only shown to client
  // tenants (canManagePartners is true solely for 'client' per
  // src/types/rootEntity.ts); partners use partnerLinks instead.
  {
    page: 'client-portal-section',
    labelKey: 'Sidebar.ClientPortal',
    href: '#',
    icon: (props: any) => <FaBriefcase {...props} />,
    archetypeGate: 'canManagePartners',
    children: [
      {
        page: 'cp-dashboard',
        labelKey: 'Sidebar.ClientPortalDashboard',
        href: '/client-portal/dashboard',
        icon: (props: any) => <LuLayoutDashboard {...props} />,
      },
      {
        page: 'cp-self-assessment',
        labelKey: 'Sidebar.ClientPortalSelfAssessment',
        href: '/client-portal/self-assessment',
        icon: (props: any) => <LuShieldCheck {...props} />,
      },
      {
        page: 'cp-frameworks',
        labelKey: 'Sidebar.ClientPortalFrameworks',
        href: '/client-portal/frameworks',
        icon: (props: any) => <LuLayers {...props} />,
      },
      {
        page: 'cp-notifications',
        labelKey: 'Sidebar.ClientPortalNotifications',
        href: '/client-portal/notifications',
        icon: (props: any) => <FaBullhorn {...props} />,
      },
      {
        page: 'cp-partners',
        labelKey: 'Sidebar.ClientPortalPartners',
        href: '/client-portal/partners',
        icon: (props: any) => <FaBuilding {...props} />,
      },
      {
        page: 'cp-requests',
        labelKey: 'Sidebar.ClientPortalRequests',
        href: '/client-portal/requests',
        icon: (props: any) => <CiViewTable {...props} />,
      },
      {
        page: 'cp-engagements',
        labelKey: 'Sidebar.ClientPortalEngagements',
        href: '/client-portal/engagements',
        icon: (props: any) => <FaTasks {...props} />,
      },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle, language }) => {
  const location = useLocation()
  const { t } = useTranslation()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const isRTL = language === 'ar'
  const [isMobileOpen, setMobileOpen] = useState(false)
  const [demoAppUrl, setDemoAppUrl] = useState<string>('http://localhost:3001/dcc')
  const { currentTenant } = useTenant()
  const archetype = currentTenant?.archetype ?? currentTenant?.rootEntityType ?? null
  // Partner Portal moved to the standalone partner_portal_frontend app, so this
  // customer app always renders the full client menu.
  const baseMenuLinks = links

  // Filter out modules disabled by the parent tenant. The /tenant/me endpoint
  // returns `disabledModules: string[]` — any top-level link whose `page` key
  // is in that list is hidden. Children are filtered recursively.
  const disabledModules = useRef<Set<string>>(new Set())
  disabledModules.current = new Set(currentTenant?.disabledModules ?? [])

  // TrustLens-only default: a client tenant with no explicit module overrides
  // starts with ONLY the TrustLens module visible. Every other top-level group
  // is hidden until opened (i.e. until `disabledModules` is set explicitly,
  // which removes the key from this default). Operator/other archetypes and
  // tenants that already have an explicit list are untouched.
  //
  // We also apply this while the tenant is still loading (currentTenant null) —
  // otherwise the full menu briefly flashes before /tenant/me resolves and the
  // filter kicks in. Only an explicitly-resolved non-client tenant shows more.
  const trustLensOnlyDefault = (!currentTenant || archetype === 'client') && archetype !== 'platform_operator';
  if (trustLensOnlyDefault && disabledModules.current.size === 0) {
    ;[
      'dashboard-section', 'process-module', 'compliance', 'enterprise-architecture',
      'Enterprise Architecture', 'enterprise-risk-management', 'catalogs', 'documents',
      'reporting', 'tenant-compliance', 'playbooks', 'user-settings', 'configuration',
      'audit', 'sup-compliance', 'organizations', 'guide', 'sub-tenant-section',
      'tenant-admin-section', 'client-portal-section',
    ].forEach((k) => disabledModules.current.add(k))
  }

  // Filter out links whose archetypeGate doesn't match the caller's archetype.
  // Per the Trust Network design, Evidence Vault + Trust Center are only for
  // `client` archetypes; Practitioners + Playbooks are only for consulting/audit.
  const caps = capabilitiesFor(archetype)

  // Phase 4: subtree-scope gate for the Tenant Admin section. /tenant/me
  // returns allowedScopes; leaves are own-only and have nothing to administer.
  const hasSubtreeScope = Boolean(
    (currentTenant as any)?.allowedScopes?.includes?.('subtree'),
  )
  // Phase 5: branch gate — the caller's tenant has a parent. /tenant/me
  // returns tenantDepth (root = 0) and parentTenantId.
  const isSubTenant = Boolean(
    ((currentTenant as any)?.tenantDepth ?? 0) > 0 ||
      (currentTenant as any)?.parentTenantId,
  )

  const filterLinks = (items: SidebarLink[]): SidebarLink[] =>
    items
      .filter((l) => !disabledModules.current.has(l.page))
      .filter((l) => !l.archetypeGate || caps[l.archetypeGate])
      .filter((l) => !l.requiresSubtree || hasSubtreeScope)
      .filter((l) => !l.requiresSubTenantDepth || isSubTenant)
      .map((l) => (l.children ? { ...l, children: filterLinks(l.children) } : l))

  const menuLinks = filterLinks(baseMenuLinks)

  useEffect(() => {
    fetch('/config.json')
      .then((res) => res.ok ? res.json() : {})
      .then((config: { DEMO_APP_URL?: string }) => {
        if (config.DEMO_APP_URL) setDemoAppUrl(config.DEMO_APP_URL.replace(/\/$/, ''))
      })
      .catch(() => {})
  }, [])

  // Which parent group is expanded (e.g. 'catalogs')
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  // Which sub-group (menu inside menu) is expanded (e.g. 'user-settings-permission', 'policy-module')
  const [openSubGroup, setOpenSubGroup] = useState<string | null>(null)

  // Keep groups open when on a child route
  useEffect(() => {
    const path = location.pathname
    if (path.startsWith('/user') || path.startsWith('/team-onboarding') || path.startsWith('/permission')) {
      setOpenGroup('user-settings')
      if (path.startsWith('/user/permissions') || path.startsWith('/permission')) {
        setOpenSubGroup('user-settings-permission')
      } else {
        setOpenSubGroup(null)
      }
    } else if (
      path === '/compliances/dashboard' ||
      path.startsWith('/reporting') ||
      path.startsWith('/standards') ||
      path.startsWith('/ticket') ||
      (path.startsWith('/assessment') && !path.startsWith('/assessment-survey')) ||
      path.startsWith('/certifications') ||
      path.startsWith('/announcements') ||
      path.startsWith('/organization-chart')
    ) {
      setOpenGroup('sup-compliance')
      setOpenSubGroup(null)
    } else if (path.startsWith('/function') || path.startsWith('/QuestionControlTree') || path.startsWith('/document-categories') || path.startsWith('/configuration/')) {
      setOpenGroup('configuration')
      setOpenSubGroup(null)
    } else if (path.startsWith('/data-inventory') || path.startsWith('/catalogs/') || path === '/catalogs' || path.startsWith('/controls') || path.startsWith('/catalogs/resources') || path.startsWith('/assets')) {
      setOpenGroup('catalogs')
      setOpenSubGroup(null)
    } else if (path.startsWith('/policy/categories') || path.startsWith('/policy/settings')) {
      setOpenGroup('configuration')
      setOpenSubGroup(null)
    } else if (path.startsWith('/standards') || path.startsWith('/objective') || path.startsWith('/compliances') || path.startsWith('/announcements') || path.startsWith('/ticket') || path.startsWith('/impactarea') || path.startsWith('/viewsurvey') || path.startsWith('/createsurvey') || path.startsWith('/updatesurvey') || path.startsWith('/surveys') || path.startsWith('/create-survey') || path.startsWith('/bankQuestions') || path.startsWith('/exportsurvey') || path.startsWith('/assessmentsurvey') || path.startsWith('/assessment-survey') || path.startsWith('/export-survey') || path.startsWith('/createQuestion') || path.startsWith('/updateQuestion') || path.startsWith('/QuestionControlTree') || path.startsWith('/policy')) {
      setOpenGroup('compliance')
      if (path.startsWith('/policy') && !path.startsWith('/policy/audit')) {
        setOpenSubGroup('policy-module')
      } else if (
        path.startsWith('/surveys') ||
        path.startsWith('/create-survey') ||
        path.startsWith('/bankQuestions') ||
        path.startsWith('/viewsurvey') ||
        path.startsWith('/createsurvey') ||
        path.startsWith('/updatesurvey') ||
        path.startsWith('/exportsurvey') ||
        path.startsWith('/assessmentsurvey') ||
        path.startsWith('/assessment-survey') ||
        path.startsWith('/export-survey') ||
        path.startsWith('/createQuestion') ||
        path.startsWith('/updateQuestion') ||
        path.startsWith('/QuestionControlTree')
      ) {
        setOpenSubGroup('survey-module')
      } else {
        setOpenSubGroup(null)
      }
    } else if (path.startsWith('/ea-canvas')) {
      setOpenGroup('business')
      setOpenSubGroup(null)
    } else if (path.startsWith('/ea/')) {
      setOpenGroup('enterprise-architecture')
      setOpenSubGroup(null)
    } else if (path.startsWith('/process-builder') || path.startsWith('/process/') || path === '/instances' || path.startsWith('/instances') || path === '/monitor' || path === '/agents' || path.startsWith('/metrics')) {
      setOpenGroup('process-module')
      setOpenSubGroup(null)
    }
  }, [location.pathname])



 
   

  useEffect(() => {
    if (isOpen && sidebarRef.current) {
      const activeItem = sidebarRef.current.querySelector('[data-active="true"]')
      activeItem?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [location.pathname, isOpen])

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`d-none d-md-flex flex-column h-100 position-relative ${
        isOpen ? 'sidebar-expanded' : 'sidebar-collapsed'
      } ${isRTL ? 'rtl' : 'ltr'}`}
     
    >
      <div className="d-flex justify-content-center align-items-center sidebar-logo-wrap">
        <Link
          to="/"
          className="d-block position-relative"
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          {isOpen ? (
            <img
              src={process.env.PUBLIC_URL + '/assets/images/2nd Logo Light.svg'}
              alt="logo"
              style={{ marginTop: '20px', marginLeft: '10px' }}
              
              className="logocomply"
            />
          ) : (
            <img
              src={process.env.PUBLIC_URL + '/assets/images/Shield Icon green.svg'}
              alt="logo"
              style={{ width: '36px', height: '36px', objectFit: 'contain', marginTop: '15px'}}
            />
          )}
        </Link>
      </div>

      <div ref={sidebarRef} className="overflow-auto" style={{ marginTop: '25px' }}>
        {menuLinks.map((link) => {
          const active = location.pathname === link.href
          const hasChildren = link.children && link.children.length > 0
          const isGroupOpen = hasChildren && openGroup === link.page

          // Parent with children (e.g. Data Inventory)
          if (hasChildren && link.children) {
            const handleParentClick = () => {
              if (!isOpen) {
                onToggle()
                setOpenGroup(link.page)
              } else {
                setOpenGroup((prev) => (prev === link.page ? null : link.page))
              }
            }
            return (
              <div key={link.page}>
                <button
                  type="button"
                  className={`sidebar-link sidebar-link-group ${isGroupOpen ? 'active' : ''} ${!isOpen ? 'justify-content-center' : ''}`}
                  onClick={handleParentClick}
                  title={!isOpen ? String(t(link.labelKey)) : ''}
                  style={{ width: '100%', border: 0, background: 'transparent', cursor: 'pointer', flexDirection: 'row', direction: 'inherit', textAlign: isRTL ? 'right' : 'left' }}
                >
                  {React.createElement(link.icon, {
                    className: "sidebar-icon mx-2",
                    style: { fontSize: !isOpen ? '1.65rem' : '1.2rem' }
                  })}
                  {isOpen && (
                    <>
                      <span className="text-truncate flex-grow-1">{t(link.labelKey)}</span>
                      {isGroupOpen ? (
                        <FaChevronDown className="sidebar-group-chevron" aria-hidden />
                      ) : (
                        <FaChevronRight className="sidebar-group-chevron" aria-hidden />
                      )}
                    </>
                  )}
                </button>
                {isOpen && isGroupOpen && (
                  <div className="sidebar-children">
                    {link.children.map((child) => {
                      const childActive =
                        child.href === "/ea-canvas"
                          ? location.pathname === "/ea-canvas" || location.pathname === "/ea-canvas/"
                          : location.pathname === child.href ||
                            (child.href !== "/" && location.pathname.startsWith(child.href))
                      const hasGrandchildren = child.children && child.children.length > 0
                      if (hasGrandchildren && child.children) {
                        const isSubOpen = openSubGroup === child.page
                        const handleSubClick = () => {
                          setOpenSubGroup((prev) => (prev === child.page ? null : child.page))
                        }
                        return (
                          <div key={child.page}>
                            <button
                              type="button"
                              className={`sidebar-link sidebar-link-child sidebar-link-subgroup ${isSubOpen ? 'active' : ''}`}
                              onClick={handleSubClick}
                              title={String(t(child.labelKey))}
                              style={{ width: '100%', border: 0, background: 'transparent', cursor: 'pointer', flexDirection: 'row', direction: 'inherit', textAlign: isRTL ? 'right' : 'left' }}
                            >
                              {React.createElement(child.icon, { className: 'sidebar-icon mx-2', style: { fontSize: '0.78rem' } })}
                              <span className="text-truncate flex-grow-1">{t(child.labelKey)}</span>
                              {isSubOpen ? (
                                <FaChevronDown className="sidebar-group-chevron" aria-hidden style={{ fontSize: '0.62rem' }} />
                              ) : (
                                <FaChevronRight className="sidebar-group-chevron" aria-hidden style={{ fontSize: '0.62rem' }} />
                              )}
                            </button>
                            {isSubOpen && (
                              <div className="sidebar-children sidebar-children-nested">
                                {child.children.map((grandchild) => {
                                  const grandActive = location.pathname === grandchild.href || (grandchild.href !== '/' && location.pathname.startsWith(grandchild.href))
                                  return (
                                    <Link
                                      key={grandchild.href}
                                      to={grandchild.href}
                                      className={`sidebar-link sidebar-link-child ${grandActive ? 'active' : ''}`}
                                      data-active={grandActive}
                                      title={String(t(grandchild.labelKey))}
                                      style={{ flexDirection: 'row', direction: 'inherit' }}
                                    >
                                      {React.createElement(grandchild.icon, { className: 'sidebar-icon mx-2', style: { fontSize: '0.72rem' } })}
                                      <span className="text-truncate">{t(grandchild.labelKey)}</span>
                                    </Link>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      }
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`sidebar-link sidebar-link-child${child.descriptionKey ? ' sidebar-link-child--stacked' : ''} ${childActive ? 'active' : ''}`}
                          data-active={childActive}
                          title={
                            child.descriptionKey
                              ? `${String(t(child.labelKey))} — ${String(t(child.descriptionKey))}`
                              : String(t(child.labelKey))
                          }
                          style={{ flexDirection: 'row', direction: 'inherit' }}
                        >
                          {React.createElement(child.icon, {
                            className: 'sidebar-icon mx-2 sidebar-link-child__icon',
                            style: { fontSize: '0.78rem' },
                          })}
                          {child.descriptionKey ? (
                            <span className="sidebar-link-label-stack flex-grow-1">
                              <span className="text-truncate d-block">{t(child.labelKey)}</span>
                              <span className="sidebar-link-desc">{t(child.descriptionKey)}</span>
                            </span>
                          ) : (
                            <span className="text-truncate">{t(child.labelKey)}</span>
                          )}
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          }

          // Handle external links
          if (link.external && (link.externalUrl != null || link.page === 'enterprise-risk-management')) {
            const baseUrl = link.page === 'enterprise-risk-management' ? demoAppUrl : (link.externalUrl || link.href)
            const token = getV2Token()
            const externalUrl = link.page === 'enterprise-risk-management' && token
              ? `${baseUrl}#auth=${encodeURIComponent(token)}`
              : baseUrl
            return (
              <div key={link.href || link.page}>
                <a
                  href={externalUrl}
                  target={link.page === 'enterprise-risk-management' ? '_self' : '_blank'}
                  rel={link.page === 'enterprise-risk-management' ? undefined : 'noopener noreferrer'}
                  className={`sidebar-link ${!isOpen ? 'justify-content-center' : ''}`}
                  title={!isOpen ? String(t(link.labelKey)) : ''}
                  style={{ flexDirection: 'row', direction: 'inherit' }}
                >
                  {React.createElement(link.icon, {
                    className: "sidebar-icon mx-2",
                    style: { fontSize: !isOpen ? '1.65rem' : '1.2rem' }
                  })}
                  {isOpen && <span className="text-truncate">{t(link.labelKey)}</span>}
                </a>
              </div>
            )
          }

          return (
            <div key={link.href}>
              <Link
                to={link.href}
                className={`sidebar-link ${active ? 'active' : ''} ${!isOpen ? 'justify-content-center' : ''}`}
                data-active={active}
                title={!isOpen ? String(t(link.labelKey)) : ''}
                style={{ flexDirection: 'row', direction: 'inherit' }}
              >
                {React.createElement(link.icon, {
                  className: "sidebar-icon mx-2",
                  style: { fontSize: !isOpen ? '1.65rem' : '1.2rem' }
                })}
                {isOpen && <span className="text-truncate">{t(link.labelKey)}</span>}
              </Link>
            </div>
          )
        })}
      </div>

      {isMobileOpen && (
        <div 
          className="position-fixed top-0 start-0 w-75 h-100 sidebar-mobile shadow-lg zindex-modal d-md-none"
          style={{ zIndex: 1050 }}
        >
          <div className="d-flex justify-content-end p-3">
            <button className="btn btn-sm btn-light" onClick={() => setMobileOpen(false)}>
              ✕
            </button>
          </div>
          <div className="px-3">
            {menuLinks.map((link) => {
              const active = location.pathname === link.href
              const hasChildren = link.children && link.children.length > 0

              // Parent with children in mobile menu
              if (hasChildren && link.children) {
                return (
                  <div key={link.page}>
                    <div className="py-2 fw-semibold text-secondary">
                      {React.createElement(link.icon, { className: "me-2" })} {t(link.labelKey)}
                    </div>
                    {link.children.map((child) => {
                      const childActive =
                        child.href === "/ea-canvas"
                          ? location.pathname === "/ea-canvas" || location.pathname === "/ea-canvas/"
                          : location.pathname === child.href ||
                            (child.href !== "/" && location.pathname.startsWith(child.href))
                      const hasGrandchildren = child.children && child.children.length > 0
                      if (hasGrandchildren && child.children) {
                        return (
                          <div key={child.page}>
                            <div className="py-1 ps-4 small fw-semibold text-muted">{t(child.labelKey)}</div>
                            {child.children.map((grandchild) => {
                              const grandActive = location.pathname === grandchild.href || (grandchild.href !== '/' && location.pathname.startsWith(grandchild.href))
                              return (
                                <Link
                                  key={grandchild.href}
                                  to={grandchild.href}
                                  className={`d-block py-2 ps-5 text-decoration-none ${grandActive ? 'fw-bold text-primary' : ''}`}
                                  onClick={() => setMobileOpen(false)}
                                >
                                  {React.createElement(grandchild.icon, { className: "me-2" })} {t(grandchild.labelKey)}
                                </Link>
                              )
                            })}
                          </div>
                        )
                      }
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`d-block py-2 ps-4 text-decoration-none ${childActive ? 'fw-bold text-primary' : ''}`}
                          onClick={() => setMobileOpen(false)}
                        >
                          <span className="d-flex align-items-start gap-2">
                            {React.createElement(child.icon, { className: 'me-2 flex-shrink-0 mt-1' })}
                            <span>
                              <span className="d-block">{t(child.labelKey)}</span>
                              {child.descriptionKey && (
                                <span className="d-block small text-muted fw-normal">{t(child.descriptionKey)}</span>
                              )}
                            </span>
                          </span>
                        </Link>
                      )
                    })}
                  </div>
                )
              }

              // Handle external links in mobile menu
              if (link.external && (link.externalUrl != null || link.page === 'enterprise-risk-management')) {
                const baseUrl = link.page === 'enterprise-risk-management' ? demoAppUrl : (link.externalUrl || link.href)
                const token = getV2Token()
                const externalUrl = link.page === 'enterprise-risk-management' && token
                  ? `${baseUrl}#auth=${encodeURIComponent(token)}`
                  : baseUrl
                return (
                  <a
                    key={link.href || link.page}
                    href={externalUrl}
                    target={link.page === 'enterprise-risk-management' ? '_self' : '_blank'}
                    rel={link.page === 'enterprise-risk-management' ? undefined : 'noopener noreferrer'}
                    className="d-block py-2 text-decoration-none"
                    onClick={() => setMobileOpen(false)}
                  >
                    {React.createElement(link.icon, { className: "me-2" })} {t(link.labelKey)}
                  </a>
                )
              }

              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`d-block py-2 text-decoration-none ${active ? 'fw-bold text-primary' : ''}`}
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="d-inline-flex align-items-center w-100">
                    {React.createElement(link.icon, { className: "me-2" })}
                    <span className="flex-grow-1">{t(link.labelKey)}</span>
                  </span>
                </Link>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default Sidebar